import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

export default function DocumentCRUD() {
  const [documents, setDocuments] = useState([]);
  const [newDocument, setNewDocument] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const navigate = useNavigate();

   const token = sessionStorage.getItem("token");

  // Fetch all documents
  const fetchDocuments = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/documents/all`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`, 
      },
    });

    if (!res.ok) {
      throw new Error("Failed to fetch documents");
    }

    const data = await res.json();
    setDocuments(data);
  } catch (error) {
    toast.error("Failed to load documents");
  }
};


  useEffect(() => {
    fetchDocuments();
  }, []);

  // 🔐 Authentication check
  useEffect(() => {
  const token = sessionStorage.getItem("token");
  const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");

  if (!token) {
    toast.error("You must be logged in to access this page.");
    navigate("/");
    return;
  }

  // ✅ check admin role
  if (!roles.includes("ROLE_ADMIN")) {
    toast.error("You are not authorized to access this page.");
    navigate("/");
    return;
  }
}, [navigate]);

  // Add new document
  const handleAdd = async () => {
  if (!newDocument.trim()) {
    toast.warning("Please enter a document name.");
    return;
  }

  try {
    const res = await fetch(`${API_BASE_URL}/api/documents/add`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ name: newDocument }),
    });

    if (!res.ok) {
      throw new Error("Add document failed");
    }

    toast.success("Document added successfully!");
    setNewDocument("");
    fetchDocuments();
  } catch (err) {
    toast.error("Failed to add document");
  }
};


  // Update document
  const handleUpdate = async (id) => {
  if (!editName.trim()) {
    toast.warning("Please enter a valid name.");
    return;
  }

  try {
    const res = await fetch(`${API_BASE_URL}/api/documents/update/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`, 
      },
      body: JSON.stringify({ name: editName }),
    });

    if (!res.ok) {
      throw new Error("Update document failed");
    }

    toast.success("Document updated!");
    setEditingId(null);
    fetchDocuments();
  } catch (err) {
    toast.error("Failed to update document");
  }
};


  // Delete document
  const handleDelete = async (id) => {
  if (!window.confirm("Delete this document?")) return;

  try {
    const res = await fetch(`${API_BASE_URL}/api/documents/delete/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`, 
      },
    });

    if (!res.ok) {
      throw new Error("Delete document failed");
    }

    toast.info("Document deleted.");
    fetchDocuments();
  } catch (err) {
    toast.error("Failed to delete document");
  }
};


  return (
    <>
      {/* ✅ Fixed Navbar */}
      <div className="fixed-top">
        <AdminNavbar />
      </div>

      {/* ✅ Full-page layout */}
      <div
        className="d-flex flex-column p-4"
        style={{
          minHeight: "100vh",
          backgroundColor: "#F5F7FB",
          marginTop: "110px",
        }}
      >
        <h3 className="fw-bold text-dark mb-4 text-start">
          Document Management
        </h3>

        <div className="row flex-grow-1 g-4">
          {/* ✅ Left column - Add New Document */}
          <div className="col-md-4 d-flex">
            <div
              className="card shadow-sm border-0 flex-fill"
              style={{
                borderRadius: "12px",
                height: "fit-content",
                alignSelf: "flex-start",
              }}
            >
              <div
                className="card-header fw-semibold"
                style={{
                  backgroundColor: "#DFE4EB",
                  color: "black",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                Add New Document
              </div>
              <div className="card-body">
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Enter document name"
                  value={newDocument}
                  onChange={(e) => setNewDocument(e.target.value)}
                />
                <button
                  className="btn text-dark w-40"
                  style={{ backgroundColor: "#DFE4EB" }}
                  onClick={handleAdd}
                >
                  <FaPlus className="me-2" />
                  Add Document
                </button>
              </div>
            </div>
          </div>

          {/* ✅ Right column - Document List */}
          <div className="col-md-8 d-flex">
            <div
              className="card shadow-sm border-0 flex-fill"
              style={{ borderRadius: "12px" }}
            >
              <div
                className="card-header fw-semibold d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "#DFE4EB",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                <span>All Documents</span>
                <span className="badge bg-secondary">
                  {documents.length} Total
                </span>
              </div>

              <div
                className="card-body p-0"
                style={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                }}
              >
                <ul className="list-group list-group-flush">
                  {documents.length === 0 && (
                    <li className="list-group-item text-muted text-center py-3">
                      No documents found.
                    </li>
                  )}
                  {documents.map((doc) => (
                    <li
                      key={doc.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      {editingId === doc.id ? (
                        <input
                          type="text"
                          className="form-control me-2"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{ maxWidth: "300px" }}
                        />
                      ) : (
                        <span className="fw-medium">{doc.name}</span>
                      )}

                      <div>
                        {editingId === doc.id ? (
                          <>
                            <button
                              className="btn btn-success btn-sm me-2"
                              onClick={() => handleUpdate(doc.id)}
                            >
                              <FaSave /> Save
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingId(null)}
                            >
                              <FaTimes /> Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn  btn-sm me-2"
                              onClick={() => {
                                setEditingId(doc.id);
                                setEditName(doc.name);
                              }}
                            >
                              <FaEdit /> 
                            </button>
                            <button
                              className="btn  btn-sm"
                              onClick={() => handleDelete(doc.id)}
                            >
                              <FaTrash /> 
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
